import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSkinSession } from './SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import {
  getPageSetupDataKey,
  storePageSetupData,
} from '../components/PageBackgroundSync';
import { getPlanFlags } from '../config/skinAnalysisFeatureFlags';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

const VALID_PLANS = ['standard', 'growth', 'pro'];

function normalizePlan(value) {
  if (value == null || value === '') return null;
  const p = String(value).toLowerCase().trim();
  return VALID_PLANS.includes(p) ? p : null;
}

const SkinAnalysisPlanContext = createContext(null);

function normalizeCaptureMode(value) {
  if (value == null || value === '') return null;
  const v = String(value).toLowerCase().trim();
  return v === 'side' || v === 'basic' ? v : null;
}

export function SkinAnalysisPlanProvider({ children }) {
  const { token } = useSkinSession();
  const [plan, setPlan] = useState(null);
  const [captureMode, setCaptureMode] = useState(null);
  const [aiMode, setAiMode] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!token || !token.trim()) {
      setPlan(null);
      setCaptureMode(null);
      setAiMode(null);
      setIsReady(true);
      return;
    }

    const dataKey = getPageSetupDataKey(token);
    let cancelled = false;
    // ai_mode is controlled by segmentation in SkinRoutinePage; do not fetch /summary here.
    setAiMode(null);

    const applyPlan = (result) => {
      if (cancelled || !result) return;
      const externalFields =
        result?.external_fields ||
        result?.data?.external_fields ||
        (Array.isArray(result) && result[0]?.external_fields);
      const rawPlan = externalFields?.plan;
      setPlan(normalizePlan(rawPlan));
    };

    const applyCaptureMode = (result) => {
      if (cancelled) return;
      const rawCaptureMode =
        result?.capture_mode ??
        result?.data?.capture_mode ??
        (Array.isArray(result) && result[0]?.capture_mode);
      setCaptureMode(normalizeCaptureMode(rawCaptureMode));
      setIsReady(true);
    };

    try {
      const stored = dataKey ? sessionStorage.getItem(dataKey) : null;
      if (stored) {
        const parsed = JSON.parse(stored);
        applyPlan(parsed);
      }
    } catch (_) {}

    const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;
    const captureUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/capture`;

    fetchWithTimeout(pageSetupUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled || !result) return;
        try {
          storePageSetupData(token, result);
        } catch (_) {}
        applyPlan(result);
      })
      .catch(() => {
        if (!cancelled) {
          setPlan(null);
        }
      });

    fetchWithTimeout(captureUrl, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => {
        if (cancelled) return;
        applyCaptureMode(result);
      })
      .catch(() => {
        if (!cancelled) {
          setCaptureMode(null);
          setIsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const flags = getPlanFlags(plan);
  // In skin flow, capture_mode is authoritative for side capture.
  // "side" => front+left+right, "basic" (or missing/invalid) => front only.
  const enableSideCapture = captureMode === 'side';
  const value = {
    plan,
    captureMode,
    aiMode,
    isReady,
    ...flags,
    ENABLE_SIDE_CAPTURE: enableSideCapture,
  };

  return (
    <SkinAnalysisPlanContext.Provider value={value}>
      {children}
    </SkinAnalysisPlanContext.Provider>
  );
}

export function useSkinAnalysisPlan() {
  const ctx = useContext(SkinAnalysisPlanContext);
  if (!ctx) {
    throw new Error('useSkinAnalysisPlan must be used within SkinAnalysisPlanProvider');
  }
  return ctx;
}
